import React, { useState, useEffect } from "react";
import ClinicForm from "./preference-form/AddNewSelfClinic";
import LocationPicker, { AddressDetails } from "../GoogleMap";
import {
  Trash2,
  Plus,
  ChevronDown,
  ChevronUp,
  MapPin,
  Building2,
} from "lucide-react";
import toast from "react-hot-toast";
import { DoctorService } from "../../services/api/doctor.service";

// Types
interface TimeFrame {
  id?: number | string;
  appointment_setting_id?: string;
  day_of_week: string;
  start_time: string;
  end_time: string;
  slot_duration: number;
}

interface ScheduleData {
  id: string;
  is_online: boolean;
  is_self_clinic: boolean;
  is_instant: boolean;
  is_monthly?: boolean;
  week_of_month?: number;
  clinic_id: string;
  doctor_id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  lat?: number;
  long?: number;
  mobile_number?: string;
  consultation_fee: number;
  sunday: boolean;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  timeFrames: TimeFrame[];
}

interface NewTimeSlot {
  start_time: string;
  end_time: string;
  slot_duration: number;
}

interface OnlineScheduleProps {
  isEditable: boolean;
  prefetchPreference: boolean;
  doctorId: string;
  onSuccess: () => void;
  updateDoctorPreference: (formData: any) => any;
  handleToggleNewScheduleType: (tyep: string) => void;
}

const DAYS_ORDER = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];
const DAY_LABELS = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];

export const OfflineSchedule: React.FC<OnlineScheduleProps> = ({
  isEditable,
  doctorId,
  onSuccess,
  updateDoctorPreference,
  handleToggleNewScheduleType,
  prefetchPreference,
}) => {
  const [isPreferenceLoading, setIsPreferenceLoading] = useState(true);
  const [selectedClinicType, setSelectedClinicType] = useState<
    "self" | "dandar"
  >("dandar");

  // State management
  const [schedulesData, setSchedulesData] = useState<ScheduleData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [addingSlots, setAddingSlots] = useState<
    Record<string, Record<string, NewTimeSlot>>
  >({});
  const [showAddForm, setShowAddForm] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [expandedAccordions, setExpandedAccordions] = useState<
    Record<string, boolean>
  >({});

  // Location Picker State
  const [showMap, setShowMap] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [currentEditingScheduleId, setCurrentEditingScheduleId] = useState<
    string | null
  >(null);
  const [address, setAddress] = useState(""); // For passing to LocationPicker

  const fetchScheduleData = async () => {
    try {
      setIsPreferenceLoading(true);
      if (doctorId) {
        const response = await DoctorService.getNewDoctorPreferences(
          doctorId,
          false,
          selectedClinicType === "self",
          false,
        );
        setSchedulesData(response || []);
      } else {
        toast.error("Doctor ID is Required");
      }
    } catch (error) {
      console.error("Error loading Doctor online schedule", error);
      toast.error("Failed to load Doctor Online Schedule");
      setError("Failed to load schedules");
    } finally {
      setIsPreferenceLoading(false);
    }
  };

  useEffect(() => {
    fetchScheduleData();
  }, [selectedClinicType, prefetchPreference]);

  // Helper functions
  const formatTime = (time: string): string => {
    if (!time) return "";
    const [hours, minutes = "00"] = time.split(":");
    const hour24 = parseInt(hours, 10);
    return `${hour24.toString().padStart(2, "0")}:${minutes.padStart(2, "0")}`;
  };

  const parseTimeInput = (timeStr: string): string => {
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours, 10);
    return `${hour}:${minutes || "00"}`;
  };

  const getTimeSlotsForDay = (scheduleId: string, day: string): TimeFrame[] => {
    const schedule = schedulesData.find((s) => s.id === scheduleId);
    if (!schedule) return [];
    return schedule.timeFrames
      .filter((frame) => frame.day_of_week === day)
      .sort((a, b) => {
        const timeA = parseInt(a.start_time.split(":")[0]);
        const timeB = parseInt(b.start_time.split(":")[0]);
        return timeA - timeB;
      });
  };

  const isDayEnabled = (scheduleId: string, day: string): boolean => {
    const schedule = schedulesData.find((s) => s.id === scheduleId);
    if (!schedule) return false;
    return schedule[day as keyof ScheduleData] as boolean;
  };

  // Event handlers
  const toggleAccordion = (scheduleId: string) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [scheduleId]: !prev[scheduleId],
    }));
  };

  const handleDayToggle = (scheduleId: string, day: string) => {
    if (!isEditable) return;

    setSchedulesData((prev) =>
      prev.map((schedule) => {
        if (schedule.id !== scheduleId) return schedule;

        const newDayStatus = !schedule[day as keyof ScheduleData];
        const updatedSchedule = { ...schedule, [day]: newDayStatus };

        // Remove timeFrames for this day if disabling
        if (!newDayStatus) {
          updatedSchedule.timeFrames = schedule.timeFrames.filter(
            (frame) => frame.day_of_week !== day,
          );
        }

        return updatedSchedule;
      }),
    );
  };

  const handleConsultationFeeChange = (scheduleId: string, value: string) => {
    if (!isEditable) return;

    const fee = parseInt(value) || 0;
    setSchedulesData((prev) =>
      prev.map((schedule) =>
        schedule.id === scheduleId
          ? { ...schedule, consultation_fee: fee }
          : schedule,
      ),
    );
  };

  const handleTimeSlotChange = (
    scheduleId: string,
    slotId: number | string,
    field: "start_time" | "end_time",
    value: string,
  ) => {
    if (!isEditable) return;

    const parsedTime = parseTimeInput(value);
    setSchedulesData((prev) =>
      prev.map((schedule) => {
        if (schedule.id !== scheduleId) return schedule;
        return {
          ...schedule,
          timeFrames: schedule.timeFrames.map((frame) =>
            frame.id === slotId ? { ...frame, [field]: parsedTime } : frame,
          ),
        };
      }),
    );
  };

  const handleDurationChange = (
    scheduleId: string,
    slotId: number | string,
    duration: number,
  ) => {
    if (!isEditable) return;

    setSchedulesData((prev) =>
      prev.map((schedule) => {
        if (schedule.id !== scheduleId) return schedule;
        return {
          ...schedule,
          timeFrames: schedule.timeFrames.map((frame) =>
            frame.id === slotId ? { ...frame, slot_duration: duration } : frame,
          ),
        };
      }),
    );
  };

  // New Handlers for Weekly/Monthly and Address
  const handleToggleSchedulingType = (scheduleId: string) => {
    if (!isEditable) return;
    setSchedulesData((prev) =>
      prev.map((schedule) =>
        schedule.id === scheduleId
          ? { ...schedule, is_monthly: !schedule.is_monthly }
          : schedule,
      ),
    );
  };

  const handleWeekChange = (scheduleId: string, week: number) => {
    if (!isEditable) return;
    setSchedulesData((prev) =>
      prev.map((schedule) =>
        schedule.id === scheduleId
          ? { ...schedule, week_of_month: week }
          : schedule,
      ),
    );
  };

  const handleClinicDetailsChange = (
    scheduleId: string,
    field: string,
    value: string,
  ) => {
    if (!isEditable) return;
    setSchedulesData((prev) =>
      prev.map((schedule) =>
        schedule.id === scheduleId ? { ...schedule, [field]: value } : schedule,
      ),
    );
  };

  const handleLocationSelect = (
    lat: string,
    lng: string,
    addressDetails: AddressDetails,
  ) => {
    if (!currentEditingScheduleId) return;

    setSchedulesData((prev) =>
      prev.map((schedule) =>
        schedule.id === currentEditingScheduleId
          ? {
              ...schedule,
              lat: parseFloat(lat),
              long: parseFloat(lng),
              address: addressDetails.fullAddress,
              city: addressDetails.city,
              state: addressDetails.state,
              pincode: addressDetails.pincode,
            }
          : schedule,
      ),
    );
    setShowMap(false);
    setCurrentEditingScheduleId(null);
  };

  const openMapForSchedule = (schedule: ScheduleData) => {
    setCurrentEditingScheduleId(schedule.id);
    setLocation({ lat: schedule.lat || 0, lng: schedule.long || 0 });
    setAddress(
      `${schedule.address} ${schedule.city} ${schedule.state} ${schedule.pincode}`,
    );
    setShowMap(true);
  };

  const handleRemoveSlot = async (
    scheduleId: string,
    slotId: number | string,
  ) => {
    if (!isEditable) return;
    if (String(slotId).includes("L")) {
      setSchedulesData((prev) =>
        prev.map((schedule) => {
          if (schedule.id !== scheduleId) return schedule;
          return {
            ...schedule,
            timeFrames: schedule.timeFrames.filter(
              (frame) => frame.id !== slotId,
            ),
          };
        }),
      );
    } else {
      setIsPreferenceLoading(true);
      try {
        const response = await DoctorService.deleteExistingTimeFrame(
          Number(slotId),
          doctorId as string,
        );
        if (response?.success) {
          toast.success("Time Frame deleted successfully");
          fetchScheduleData();
        } else {
          toast.success(`Error while deleting the Time frame`);
        }
      } catch (error: any) {
        console.error(error);
        toast.error(error.message);
      }
    }
  };

  const handleShowAddForm = (scheduleId: string, day: string) => {
    if (!isEditable) return;

    setShowAddForm((prev) => ({
      ...prev,
      [scheduleId]: { ...prev[scheduleId], [day]: true },
    }));
    setAddingSlots((prev) => ({
      ...prev,
      [scheduleId]: {
        ...prev[scheduleId],
        [day]: { start_time: "09:00", end_time: "17:00", slot_duration: 30 },
      },
    }));
  };

  const handleAddSlot = (scheduleId: string, day: string) => {
    if (!isEditable) return;

    const addingSlot = addingSlots[scheduleId]?.[day];
    if (!addingSlot) return;

    const newSlot: TimeFrame = {
      id: `L-${Date.now()}`,
      appointment_setting_id: scheduleId,
      day_of_week: day,
      start_time: parseTimeInput(addingSlot.start_time),
      end_time: parseTimeInput(addingSlot.end_time),
      slot_duration: addingSlot.slot_duration,
    };

    setSchedulesData((prev) =>
      prev.map((schedule) => {
        if (schedule.id !== scheduleId) return schedule;
        return {
          ...schedule,
          timeFrames: [...schedule.timeFrames, newSlot],
        };
      }),
    );

    // Reset form
    setShowAddForm((prev) => ({
      ...prev,
      [scheduleId]: { ...prev[scheduleId], [day]: false },
    }));
    setAddingSlots((prev) => {
      const newState = { ...prev };
      if (newState[scheduleId]) {
        delete newState[scheduleId][day];
      }
      return newState;
    });
  };

  const handleCancelAddSlot = (scheduleId: string, day: string) => {
    setShowAddForm((prev) => ({
      ...prev,
      [scheduleId]: { ...prev[scheduleId], [day]: false },
    }));
    setAddingSlots((prev) => {
      const newState = { ...prev };
      if (newState[scheduleId]) {
        delete newState[scheduleId][day];
      }
      return newState;
    });
  };

  const handleAddSlotInputChange = (
    scheduleId: string,
    day: string,
    field: keyof NewTimeSlot,
    value: string | number,
  ) => {
    setAddingSlots((prev) => ({
      ...prev,
      [scheduleId]: {
        ...prev[scheduleId],
        [day]: { ...prev[scheduleId]?.[day], [field]: value },
      },
    }));
  };

  const handleUpdateExistingPreference = async (scheduleId: string) => {
    const scheduleToUpdate = schedulesData.find((s) => s.id === scheduleId);
    if (!scheduleToUpdate) return;

    try {
      setIsPreferenceLoading(true);
      const isUpdated = await updateDoctorPreference({
        id: scheduleToUpdate.id,
        clinic_id: scheduleToUpdate.clinic_id,
        doctor_id: scheduleToUpdate.doctor_id,
        is_online: scheduleToUpdate.is_online,
        is_self_clinic: scheduleToUpdate.is_self_clinic,
        is_instant: scheduleToUpdate.is_instant,
        is_monthly: scheduleToUpdate.is_monthly,
        week_of_month: scheduleToUpdate.week_of_month,
        name: scheduleToUpdate.name,
        address: scheduleToUpdate.address,
        city: scheduleToUpdate.city,
        state: scheduleToUpdate.state,
        pincode: scheduleToUpdate.pincode,
        lat: scheduleToUpdate.lat,
        long: scheduleToUpdate.long,
        mobile_number:
          scheduleToUpdate.mobile_number ||
          (scheduleToUpdate as any).mobileNumber ||
          "",
        consultation_fee: scheduleToUpdate.consultation_fee,
        sunday: scheduleToUpdate.sunday,
        monday: scheduleToUpdate.monday,
        tuesday: scheduleToUpdate.tuesday,
        wednesday: scheduleToUpdate.wednesday,
        thursday: scheduleToUpdate.thursday,
        friday: scheduleToUpdate.friday,
        saturday: scheduleToUpdate.saturday,
        TimeFrame: scheduleToUpdate.timeFrames.map((tf) => {
          const numId = Number(tf.id);
          const hasValidId = !isNaN(numId) && !String(tf.id).includes("L");
          return {
            ...(hasValidId ? { id: numId } : {}),
            appointment_setting_id:
              tf.appointment_setting_id || scheduleToUpdate.id,
            day_of_week: tf.day_of_week,
            start_time: tf.start_time,
            end_time: tf.end_time,
            slot_duration: Number(tf.slot_duration),
          };
        }),
      });
      if (isUpdated) {
        await fetchScheduleData();
        onSuccess();
      }
    } catch (error) {
      toast.error("Error updating preference");
    } finally {
      setIsPreferenceLoading(false);
    }
  };

  if (isPreferenceLoading) {
    return (
      <div className="w-full mx-auto">
        <div className="animate-pulse space-y-4">
          {[1].map((i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-4">
              <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="h-10 bg-gray-200 rounded mb-6"></div>
              <div className="space-y-4">
                {[1].map((j) => (
                  <div key={j} className="space-y-2">
                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 w-full mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-red-800 text-sm">{error}</p>
          <button
            onClick={fetchScheduleData}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (schedulesData.length === 0) {
    return (
      <div className="w-full mx-auto">
        <SelectClinicType
          clinicType={selectedClinicType}
          setClinicType={setSelectedClinicType}
        />
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Building2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Schedules Available
          </h3>
          <p className="text-gray-600 text-sm mb-6">
            Create your first Offline consultation schedule to get started.
          </p>

          {isEditable && (
            <button
              onClick={() =>
                handleToggleNewScheduleType(
                  selectedClinicType === "self"
                    ? "Self Clinic"
                    : "Dandar Clinic",
                )
              }
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2 mx-auto"
            >
              <Plus size={16} />
              Add Schedule
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-6">
      <SelectClinicType
        clinicType={selectedClinicType}
        setClinicType={setSelectedClinicType}
      />

      {schedulesData.map((schedule) => (
        <div
          key={schedule.id}
          className="border border-gray-200 rounded-lg overflow-hidden  mb-5"
        >
          {/* Accordion Header */}
          <div
            className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => toggleAccordion(schedule.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-gray-600" />
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {schedule.name}
                    </h3>
                    <div className="flex items-center gap-3 text-sm text-gray-600 mt-1 flex-wrap">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        <span>
                          {schedule.address}, {schedule.city}, {schedule.state} -{" "}
                          {schedule.pincode}
                        </span>
                      </div>
                      {(schedule.mobile_number ||
                        (schedule as any).mobileNumber) && (
                        <div className="flex items-center gap-1 text-gray-500">
                          <span>📞</span>
                          <span>
                            {schedule.mobile_number ||
                              (schedule as any).mobileNumber}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-green-600">
                    ₹{schedule.consultation_fee}
                  </div>
                  <div className="text-xs text-gray-500">Consultation Fee</div>
                </div>
                {expandedAccordions[schedule.id] ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>
          </div>

          {/* Accordion Content */}
          {expandedAccordions[schedule.id] && (
            <div className="p-6">
              {/* Scheduling Type Section */}
              <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-medium text-gray-800 text-sm">
                      Scheduling Type
                    </h4>
                    <p className="text-xs text-gray-500">
                      Current:{" "}
                      <span className="font-semibold text-emerald-600">
                        {schedule.is_monthly ? "Monthly" : "Weekly"}
                      </span>
                    </p>
                  </div>

                  <button
                    onClick={() => handleToggleSchedulingType(schedule.id)}
                    disabled={!isEditable}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                      schedule.is_monthly ? "bg-emerald-600" : "bg-gray-200"
                    } ${!isEditable ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        schedule.is_monthly ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                {schedule.is_monthly && (
                  <div>
                    <label className="text-sm font-medium text-[#3F5260] mb-2 block">
                      Select Week of Month
                    </label>
                    <div className="flex items-center gap-2 flex-wrap">
                      {[1, 2, 3, 4, 5].map((week) => (
                        <button
                          key={week}
                          onClick={() => handleWeekChange(schedule.id, week)}
                          disabled={!isEditable}
                          className={`px-4 py-2 text-sm font-medium rounded-lg border transition-all ${
                            schedule.week_of_month === week
                              ? "bg-emerald-600 text-white border-emerald-600"
                              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                          } ${!isEditable ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          Week {week}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Clinic Address Section for Self Clinic */}
              {schedule.is_self_clinic && (
                <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                    <h4 className="font-medium text-gray-700 text-sm">
                      Clinic Details
                    </h4>
                  </div>
                  <ClinicForm
                    formData={{
                      name: schedule.name,
                      address: schedule.address,
                      city: schedule.city,
                      state: schedule.state,
                      pincode: schedule.pincode,
                      mobile_number:
                        schedule.mobile_number ||
                        (schedule as any).mobileNumber ||
                        "",
                    }}
                    onChange={(field, value) =>
                      handleClinicDetailsChange(schedule.id, field, value)
                    }
                    isGenerating={!isEditable}
                  />
                  {isEditable && (
                    <div className="p-4 bg-white border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => openMapForSchedule(schedule)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2"
                      >
                        <MapPin size={16} /> Locate on Map
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Mobile Number Section for Dandar Clinic */}
              {!schedule.is_self_clinic && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-600 mb-2">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    maxLength={10}
                    value={
                      schedule.mobile_number ||
                      (schedule as any).mobileNumber ||
                      ""
                    }
                    onChange={(e) =>
                      handleClinicDetailsChange(
                        schedule.id,
                        "mobile_number",
                        e.target.value
                      )
                    }
                    disabled={!isEditable}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                      !isEditable ? "bg-gray-50 cursor-not-allowed" : "bg-white"
                    }`}
                    placeholder="Enter clinic mobile number"
                  />
                </div>
              )}

              {/* OP Rate Section */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  OP Rate
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    ₹
                  </span>
                  <input
                    type="number"
                    value={schedule.consultation_fee}
                    onChange={(e) =>
                      handleConsultationFeeChange(schedule.id, e.target.value)
                    }
                    disabled={!isEditable}
                    className={`w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                      !isEditable ? "bg-gray-50 cursor-not-allowed" : "bg-white"
                    }`}
                    placeholder="Enter consultation fee"
                  />
                </div>
              </div>

              {/* Days and Session Timings */}
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-medium text-gray-600">Days</h3>
                  <h3 className="text-sm font-medium text-gray-600">
                    Session Timings
                  </h3>
                  <h3 className="text-sm font-medium text-gray-600">
                    Durations
                  </h3>
                </div>

                {DAYS_ORDER.map((day) => {
                  const dayEnabled = isDayEnabled(schedule.id, day);
                  const timeSlots = getTimeSlotsForDay(schedule.id, day);
                  const isShowingAddForm = showAddForm[schedule.id]?.[day];
                  const addingSlot = addingSlots[schedule.id]?.[day];

                  return (
                    <div key={day} className="space-y-3">
                      <div className="flex items-start gap-4">
                        <button
                          className={`px-3 py-1 rounded text-sm font-medium min-w-[50px] transition-colors ${
                            dayEnabled
                              ? "bg-green-600 text-white"
                              : "bg-gray-300 text-gray-600"
                          } ${
                            isEditable
                              ? "hover:opacity-80 cursor-pointer"
                              : "cursor-default"
                          }`}
                          onClick={() => handleDayToggle(schedule.id, day)}
                          disabled={!isEditable}
                        >
                          {DAY_LABELS[day as keyof typeof DAY_LABELS]}
                        </button>

                        <div className="flex-1 space-y-2">
                          {dayEnabled && (
                            <>
                              {/* Existing Time Slots */}
                              {timeSlots.map((slot) => (
                                <div
                                  key={slot.id}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    type="time"
                                    value={formatTime(slot.start_time)}
                                    onChange={(e) =>
                                      handleTimeSlotChange(
                                        schedule.id,
                                        slot.id!,
                                        "start_time",
                                        e.target.value,
                                      )
                                    }
                                    disabled={!isEditable}
                                    className={`flex-1 px-3 py-2 border border-gray-300 rounded text-sm ${
                                      !isEditable
                                        ? "bg-gray-50 cursor-not-allowed"
                                        : "bg-white focus:ring-2 focus:ring-green-500"
                                    }`}
                                  />
                                  <span className="text-gray-400">→</span>
                                  <input
                                    type="time"
                                    value={formatTime(slot.end_time)}
                                    onChange={(e) =>
                                      handleTimeSlotChange(
                                        schedule.id,
                                        slot.id!,
                                        "end_time",
                                        e.target.value,
                                      )
                                    }
                                    disabled={!isEditable}
                                    className={`flex-1 px-3 py-2 border border-gray-300 rounded text-sm ${
                                      !isEditable
                                        ? "bg-gray-50 cursor-not-allowed"
                                        : "bg-white focus:ring-2 focus:ring-green-500"
                                    }`}
                                  />
                                  <select
                                    value={slot.slot_duration}
                                    onChange={(e) =>
                                      handleDurationChange(
                                        schedule.id,
                                        slot.id!,
                                        parseInt(e.target.value),
                                      )
                                    }
                                    disabled={!isEditable}
                                    className={`px-2 py-2 border border-gray-300 rounded text-sm ${
                                      !isEditable
                                        ? "bg-gray-50 cursor-not-allowed"
                                        : "bg-white focus:ring-2 focus:ring-green-500"
                                    }`}
                                  >
                                    {DURATION_OPTIONS.map((duration) => (
                                      <option key={duration} value={duration}>
                                        {duration}m
                                      </option>
                                    ))}
                                  </select>
                                  {isEditable && (
                                    <button
                                      onClick={() =>
                                        handleRemoveSlot(schedule.id, slot.id!)
                                      }
                                      className="p-1 text-red-500 hover:text-red-700"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  )}
                                </div>
                              ))}

                              {/* Add New Slot Form */}
                              {isShowingAddForm && addingSlot && (
                                <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
                                  <input
                                    type="time"
                                    value={addingSlot.start_time}
                                    onChange={(e) =>
                                      handleAddSlotInputChange(
                                        schedule.id,
                                        day,
                                        "start_time",
                                        e.target.value,
                                      )
                                    }
                                    className="flex-1 px-3 py-2 border border-green-300 rounded text-sm focus:ring-2 focus:ring-green-500"
                                  />
                                  <span className="text-gray-400">→</span>
                                  <input
                                    type="time"
                                    value={addingSlot.end_time}
                                    onChange={(e) =>
                                      handleAddSlotInputChange(
                                        schedule.id,
                                        day,
                                        "end_time",
                                        e.target.value,
                                      )
                                    }
                                    className="flex-1 px-3 py-2 border border-green-300 rounded text-sm focus:ring-2 focus:ring-green-500"
                                  />
                                  <select
                                    value={addingSlot.slot_duration}
                                    onChange={(e) =>
                                      handleAddSlotInputChange(
                                        schedule.id,
                                        day,
                                        "slot_duration",
                                        parseInt(e.target.value),
                                      )
                                    }
                                    className="px-2 py-2 border border-green-300 rounded text-sm focus:ring-2 focus:ring-green-500"
                                  >
                                    {DURATION_OPTIONS.map((duration) => (
                                      <option key={duration} value={duration}>
                                        {duration}m
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() =>
                                      handleAddSlot(schedule.id, day)
                                    }
                                    className="px-3 py-2 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleCancelAddSlot(schedule.id, day)
                                    }
                                    className="px-3 py-2 bg-gray-400 text-white rounded text-xs hover:bg-gray-500"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}

                              {/* Add Slot Button */}
                              {isEditable && !isShowingAddForm && (
                                <button
                                  onClick={() =>
                                    handleShowAddForm(schedule.id, day)
                                  }
                                  className="w-full py-2 bg-green-100 text-green-700 rounded border border-green-200 hover:bg-green-150 text-sm font-medium flex items-center justify-center gap-1"
                                >
                                  <Plus size={16} />
                                  Add Slot
                                </button>
                              )}
                            </>
                          )}

                          {!dayEnabled && (
                            <div className="text-sm text-gray-500 italic py-2">
                              Day is disabled. Enable edit and Click the day
                              button to enable.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Save Button - Only show when editable */}
              {isEditable && (
                <div className="mt-8 pt-4 border-t">
                  <button
                    onClick={() => handleUpdateExistingPreference(schedule.id)}
                    className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {isEditable && (
        <button
          onClick={() =>
            handleToggleNewScheduleType(
              selectedClinicType === "self" ? "Self Clinic" : "Dandar Clinic",
            )
          }
          className="w-full justify-center px-6 py-3 bg-white text-green-600 rounded-lg border border-green-600/60 font-medium flex items-center gap-2 mt-6 mx-auto"
        >
          <Plus size={16} />
          Add Schedule
        </button>
      )}

      {showMap && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <LocationPicker
            location={location}
            setLocation={setLocation}
            address={address}
            setShowMap={setShowMap}
            onLocationSelect={handleLocationSelect}
          />
        </div>
      )}
    </div>
  );
};

const SelectClinicType = ({
  clinicType,
  setClinicType,
}: {
  clinicType: string;
  setClinicType: (type: "self" | "dandar") => void;
}) => {
  return (
    <div className="mb-5 p-2 flex flex-col gap-1 items-start justify-start">
      <label
        htmlFor="clinicType"
        className="text-sm font-medium text-[#3F5260]"
      >
        Clinic type
      </label>
      <select
        id="clinicType"
        name="clinicType"
        value={clinicType}
        onChange={(e) => setClinicType(e.target.value as "dandar" | "self")}
        className="border border-[#D0D5DE] w-[220px] text-black font-light text-sm p-1 pr-10 outline-none rounded"
      >
        <option value="dandar">Dandar Clinics</option>
        <option value="self">Self Clinics</option>
      </select>
    </div>
  );
};
